import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../api';

// ── Shared styles ─────────────────────────────────────────────────────────────
const card = { background: 'white', border: '1px solid #d8e4dd', borderRadius: '8px', padding: '16px', boxShadow: '0 12px 30px rgba(24,47,38,0.08)' };
const muted = { color: '#61726b', fontSize: '13px' };
const h2s   = { margin: '0 0 10px', color: '#064734', fontSize: '17px' };
const inputS= { border: '1px solid #d8e4dd', borderRadius: '8px', padding: '10px 11px', background: 'white', color: '#12231d', fontWeight: 800, width: '100%', boxSizing: 'border-box' };

function euro(v) { return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }
function toneColor(t) { return t === 'red' || t === 'over' ? '#b42318' : t === 'amber' || t === 'critical' ? '#a16207' : t === 'warning' ? '#b45309' : t === 'inactive' ? '#64748b' : '#0a6f4f'; }
function btn(bg, extra) { return { background: bg, color: 'white', border: 'none', borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', fontWeight: 900, ...extra }; }
function smBtn(bg) { return { ...btn(bg), padding: '7px 9px', fontSize: '12px' }; }

function Notice({ tone, children }) {
  const red = tone === 'red';
  return <div style={{ background: red ? '#fff1ef' : '#dff3e9', color: red ? '#9f1d12' : '#064734', border: `1px solid ${red ? '#ffc9c2' : '#a7d9c2'}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontWeight: 800 }}>{children}</div>;
}

function Stat({ title, value, hint, tone = 'slate' }) {
  const colors = { green: '#0a6f4f', amber: '#a16207', red: '#b42318', blue: '#1d4ed8', slate: '#12231d' };
  return <div style={card}><div style={{ color: '#61726b', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>{title}</div><div style={{ color: colors[tone] || colors.slate, fontSize: '24px', fontWeight: 950, marginTop: '6px' }}>{value}</div><div style={{ ...muted, marginTop: '3px' }}>{hint}</div></div>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function monthBounds(offsetMonths = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths);
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(lastDay).padStart(2, '0')}` };
}

const WARNING_LABELS = { ok: 'Im Rahmen', warning: 'Achtung', critical: 'Kritisch', over: 'Überschritten' };

// ── Tab 1: existing monthly budget goals ─────────────────────────────────────
function MonatsZieleTab({ data, onReload }) {
  const [form, setForm] = useState({ kategorie: '', monatliches_limit: '', aktiv: true, notiz: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState('');
  const [err, setErr]   = useState('');

  const categoryOptions = useMemo(() => data?.categories || [], [data]);
  const statusItems = data?.status?.items || [];
  const activeItems = data?.status?.activeItems || [];
  const totals      = data?.status?.totals || {};

  useEffect(() => {
    if (!form.kategorie && data?.categories?.[0]) setForm(f => ({ ...f, kategorie: data.categories[0].name }));
  }, [data]); // eslint-disable-line

  async function save(e) {
    e.preventDefault(); setBusy(true); setErr(''); setMsg('');
    try {
      await api.post('/budgets', { kategorie: form.kategorie, monatliches_limit: Number(form.monatliches_limit), aktiv: form.aktiv, notiz: form.notiz });
      setMsg(`Budget für ${form.kategorie} gespeichert.`);
      setForm(f => ({ ...f, monatliches_limit: '', notiz: '' }));
      onReload();
    } catch (e) { setErr(e.response?.data?.error || e.message || 'Fehler'); }
    finally { setBusy(false); }
  }

  async function remove(goal) {
    if (!window.confirm(`Budget für ${goal.kategorie} löschen?`)) return;
    await api.delete(`/budgets/${goal.id}`); setMsg(`Budget für ${goal.kategorie} gelöscht.`); onReload();
  }

  function edit(goal) { setForm({ kategorie: goal.kategorie, monatliches_limit: String(goal.monatliches_limit), aktiv: goal.aktiv !== 0, notiz: goal.notiz || '' }); }

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
        <Stat title="Aktive Budgets"  value={activeItems.length}        hint="Monatsziele"           tone="green" />
        <Stat title="Monatslimit"     value={euro(totals.limit)}         hint={data?.status?.monat||'-'} tone="blue" />
        <Stat title="Ausgaben"        value={euro(totals.ausgaben)}      hint={`${Math.round(((totals.ausgaben||0)/Math.max(totals.limit||1,1))*100)}% genutzt`} tone={totals.red?'red':totals.amber?'amber':'green'} />
        <Stat title="Überzogen"       value={euro(totals.ueberzogen)}    hint={`${totals.red||0} rote Ampel(n)`} tone={totals.red?'red':'green'} />
      </div>
      {err && <Notice tone="red">{err}</Notice>}
      {msg && <Notice tone="green">{msg}</Notice>}
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '12px', alignItems: 'start' }}>
        <section style={card}>
          <h2 style={h2s}>Budget speichern</h2>
          <form onSubmit={save} style={{ display: 'grid', gap: '9px' }}>
            <select value={form.kategorie} onChange={e => setForm({ ...form, kategorie: e.target.value })} style={inputS}>
              {categoryOptions.map(c => <option key={c.name} value={c.name}>{c.icon} {c.name}</option>)}
            </select>
            <input value={form.monatliches_limit} onChange={e => setForm({ ...form, monatliches_limit: e.target.value })} placeholder="Monatslimit, z.B. 250" type="number" min="1" step="0.01" style={inputS} />
            <input value={form.notiz} onChange={e => setForm({ ...form, notiz: e.target.value })} placeholder="Notiz optional" style={inputS} />
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', color: '#12231d', fontWeight: 900 }}>
              <input type="checkbox" checked={form.aktiv} onChange={e => setForm({ ...form, aktiv: e.target.checked })} /> Budget aktiv
            </label>
            <button disabled={busy} style={btn('#0a6f4f')}>{busy ? 'Speichere...' : 'Speichern'}</button>
          </form>
        </section>
        <section style={card}>
          <h2 style={h2s}>Budget-Ampeln</h2>
          {!statusItems.length && <div style={muted}>Noch keine Budgets gesetzt.</div>}
          {statusItems.map(item => (
            <div key={item.id} style={{ borderTop: '1px solid #eef4f1', padding: '10px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#12231d', fontWeight: 950 }}>{item.kategorie}</div>
                  <div style={muted}>{euro(item.ausgaben)} von {euro(item.monatliches_limit)} · {item.prozent}% · {item.status}</div>
                  <div style={{ height: '9px', background: '#eef4f1', borderRadius: '999px', overflow: 'hidden', marginTop: '8px' }}>
                    <div style={{ width: `${Math.min(100, item.prozent || 0)}%`, height: '100%', background: toneColor(item.tone) }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: toneColor(item.tone), fontWeight: 950 }}>{item.ueberzogen > 0 ? `+${euro(item.ueberzogen)}` : euro(item.verbleibend)}</div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                    <button onClick={() => edit(item)} style={smBtn('#2563eb')}>Edit</button>
                    <button onClick={() => remove(item)} style={smBtn('#b42318')}>Del</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>
      </div>
    </>
  );
}

// ── Tab 2: category budgets with flexible period ──────────────────────────────
function KategorieBudgetsTab({ categories: categoryOptions }) {
  const def = monthBounds();
  const [from, setFrom] = useState(def.from);
  const [to,   setTo]   = useState(def.to);
  const [catData, setCatData] = useState(null);
  const [filter, setFilter]   = useState('all');
  const [sort,   setSort]     = useState('category');
  const [form,   setForm]     = useState({ category: '', budget_amount: '' });
  const [editId, setEditId]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [msg,  setMsg]    = useState('');
  const [err,  setErr]    = useState('');

  const load = useCallback(async () => {
    if (!from || !to || from > to) return;
    setErr('');
    try {
      const r = await api.get('/budgets/categories', { params: { from, to } });
      setCatData(r.data);
    } catch (e) { setErr(e.response?.data?.error || e.message || 'Ladefehler'); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (categoryOptions?.length && !form.category) setForm(f => ({ ...f, category: categoryOptions[0]?.name || '' })); }, [categoryOptions]); // eslint-disable-line

  async function saveEntry(e) {
    e.preventDefault(); setBusy(true); setErr(''); setMsg('');
    try {
      if (editId) {
        await api.put(`/budgets/categories/${editId}`, { budget_amount: Number(form.budget_amount) });
        setMsg('Budget aktualisiert.');
      } else {
        await api.post('/budgets/categories', { period_start: from, period_end: to, category: form.category, budget_amount: Number(form.budget_amount) });
        setMsg(`Budget für ${form.category} gespeichert.`);
      }
      setForm(f => ({ ...f, budget_amount: '' }));
      setEditId(null);
      await load();
    } catch (e) { setErr(e.response?.data?.error || e.message || 'Fehler'); }
    finally { setBusy(false); }
  }

  async function removeEntry(item) {
    if (!window.confirm(`Budget für ${item.category} löschen?`)) return;
    await api.delete(`/budgets/categories/${item.id}`); setMsg(`Budget für ${item.category} gelöscht.`); setEditId(null); await load();
  }

  function startEdit(item) { setEditId(item.id); setForm(f => ({ ...f, category: item.category, budget_amount: String(item.budget) })); }

  const items = catData?.categories || [];
  const totals = catData?.totals || {};

  const filtered = useMemo(() => {
    let list = [...items];
    if (filter === 'with')     list = list.filter(c => c.budget !== null);
    if (filter === 'without')  list = list.filter(c => c.budget === null);
    if (filter === 'critical') list = list.filter(c => c.warning_level === 'critical' || c.warning_level === 'over');
    if (filter === 'over')     list = list.filter(c => c.warning_level === 'over');
    if (sort === 'budget')    list.sort((a, b) => (b.budget || 0) - (a.budget || 0));
    else if (sort === 'pct')  list.sort((a, b) => (b.projected_pct || 0) - (a.projected_pct || 0));
    else if (sort === 'remaining') list.sort((a, b) => (a.remaining ?? Infinity) - (b.remaining ?? Infinity));
    else list.sort((a, b) => a.category.localeCompare(b.category));
    return list;
  }, [items, filter, sort]);

  const criticalCount = items.filter(c => c.warning_level === 'over' || c.warning_level === 'critical').length;

  return (
    <>
      {/* Period picker */}
      <section style={{ ...card, marginBottom: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div>
            <div style={{ ...muted, marginBottom: '4px' }}>Von</div>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...inputS, width: '160px' }} />
          </div>
          <div>
            <div style={{ ...muted, marginBottom: '4px' }}>Bis</div>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...inputS, width: '160px' }} />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[-1, 0, 1].map(off => {
              const b = monthBounds(off);
              const label = off === 0 ? 'Aktueller Monat' : off === -1 ? 'Vormonat' : 'Nächster Monat';
              return <button key={off} onClick={() => { setFrom(b.from); setTo(b.to); }} style={btn('#334155', { fontSize: '12px', padding: '9px 10px' })}>{label}</button>;
            })}
          </div>
        </div>
      </section>

      {err && <Notice tone="red">{err}</Notice>}
      {msg && <Notice tone="green">{msg}</Notice>}

      {/* Summary tiles */}
      {catData && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
          <Stat title="Gesamtbudget"   value={euro(totals.budget)}    hint={`${items.filter(c=>c.budget!==null).length} Kategorien`} tone="blue" />
          <Stat title="Ausgegeben"     value={euro(totals.spent)}     hint="tatsächlich gebucht"      tone="slate" />
          <Stat title="Geplant"        value={euro(totals.planned)}   hint="ausstehende Abbuchungen"  tone="amber" />
          <Stat title="Voraussichtlich verbleibend" value={euro(totals.remaining)} hint={criticalCount ? `${criticalCount} kritisch/überschritten` : 'Im Rahmen'} tone={criticalCount ? 'red' : 'green'} />
        </div>
      )}

      {/* Filters + sort */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'center' }}>
        {[['all','Alle'],['with','Mit Budget'],['without','Ohne Budget'],['critical','Kritisch'],['over','Überschritten']].map(([k,l]) => (
          <button key={k} onClick={() => setFilter(k)} style={btn(filter===k?'#0a6f4f':'#d8e4dd', { color: filter===k?'white':'#12231d', fontSize:'12px', padding:'7px 10px' })}>{l}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={muted}>Sortierung:</span>
          <select value={sort} onChange={e => setSort(e.target.value)} style={{ ...inputS, width: 'auto', padding: '7px 8px', fontSize: '12px' }}>
            <option value="category">Kategorie</option>
            <option value="pct">Verbrauch %</option>
            <option value="budget">Budget</option>
            <option value="remaining">Verbleibend</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '12px', alignItems: 'start' }}>
        {/* Add/edit form */}
        <section style={card}>
          <h2 style={h2s}>{editId ? 'Budget bearbeiten' : '+ Kategorie-Budget'}</h2>
          <form onSubmit={saveEntry} style={{ display: 'grid', gap: '9px' }}>
            {!editId && (
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputS}>
                {(categoryOptions || []).map(c => <option key={c.name} value={c.name}>{c.icon} {c.name}</option>)}
                <option value="Unkategorisiert">⚠ Unkategorisiert</option>
              </select>
            )}
            {editId && <div style={{ ...muted, padding: '8px', background: '#f8fffb', borderRadius: '6px' }}>Kategorie: <strong>{form.category}</strong></div>}
            <input value={form.budget_amount} onChange={e => setForm({ ...form, budget_amount: e.target.value })} placeholder="Budget in €, z.B. 300" type="number" min="0.01" step="0.01" style={inputS} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button disabled={busy} style={btn('#0a6f4f', { flex: 1 })}>{busy ? 'Speichere...' : editId ? 'Aktualisieren' : 'Hinzufügen'}</button>
              {editId && <button type="button" onClick={() => { setEditId(null); setForm(f => ({ ...f, budget_amount: '' })); }} style={btn('#64748b')}>Abbrechen</button>}
            </div>
          </form>
          <div style={{ ...muted, marginTop: '10px', fontSize: '12px' }}>
            Zeitraum: <strong>{from}</strong> – <strong>{to}</strong>
          </div>
        </section>

        {/* Category cards */}
        <div>
          {!catData && <div style={{ ...card, ...muted }}>Lade Daten...</div>}
          {catData && !filtered.length && <div style={{ ...card, ...muted }}>Keine Kategorien für diesen Filter.</div>}
          <div style={{ display: 'grid', gap: '8px' }}>
            {filtered.map(item => <CategoryCard key={item.category} item={item} onEdit={() => startEdit(item)} onDelete={() => removeEntry(item)} />)}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Category card ─────────────────────────────────────────────────────────────
function CategoryCard({ item, onEdit, onDelete }) {
  const hasBudget  = item.budget !== null;
  const warnColor  = toneColor(item.warning_level);
  const istPct     = item.usage_pct ?? 0;
  const projPct    = item.projected_pct ?? 0;
  const isOver     = item.warning_level === 'over';
  const isCritical = item.warning_level === 'critical' || item.warning_level === 'warning';

  return (
    <div style={{ ...card, borderColor: isOver ? '#fecaca' : isCritical ? '#fed7aa' : '#d8e4dd', background: isOver ? '#fff7f5' : isCritical ? '#fffbeb' : 'white' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
            <strong style={{ color: '#12231d', fontSize: '15px' }}>{item.category}</strong>
            {hasBudget && (
              <span style={{ fontSize: '11px', fontWeight: 900, background: isOver?'#fee2e2':isCritical?'#fef9c3':'#dff3e9', color: warnColor, padding: '2px 7px', borderRadius: '999px' }}>
                {WARNING_LABELS[item.warning_level]}
              </span>
            )}
          </div>
          {hasBudget && (
            <>
              {/* Progress bar: IST (solid) + GEPLANT (striped) */}
              <div style={{ height: '10px', background: '#eef4f1', borderRadius: '999px', overflow: 'hidden', position: 'relative', marginBottom: '6px' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.min(100, istPct)}%`, background: warnColor }} />
                <div style={{ position: 'absolute', left: `${Math.min(100, istPct)}%`, top: 0, height: '100%', width: `${Math.min(100 - Math.min(100, istPct), projPct - istPct)}%`, background: warnColor, opacity: 0.35 }} />
              </div>
              <div style={muted}>
                IST: {item.usage_pct}% &nbsp;|&nbsp; Mit Geplant: {item.projected_pct}%
              </div>
            </>
          )}
          {!hasBudget && <div style={muted}>Kein Budget gesetzt</div>}
        </div>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ textAlign: 'right', minWidth: '90px' }}>
            <div style={{ ...muted, fontSize: '11px' }}>Ausgegeben</div>
            <div style={{ fontWeight: 950, color: '#12231d' }}>{euro(item.spent)}</div>
          </div>
          {(item.planned > 0) && (
            <div style={{ textAlign: 'right', minWidth: '90px' }}>
              <div style={{ ...muted, fontSize: '11px' }}>Geplant</div>
              <div style={{ fontWeight: 950, color: '#a16207' }}>+{euro(item.planned)}</div>
            </div>
          )}
          {(item.uncertain > 0) && (
            <div style={{ textAlign: 'right', minWidth: '90px' }}>
              <div style={{ ...muted, fontSize: '11px' }}>Unsicher</div>
              <div style={{ fontWeight: 950, color: '#64748b' }}>±{euro(item.uncertain)}</div>
            </div>
          )}
          {hasBudget && (
            <div style={{ textAlign: 'right', minWidth: '90px' }}>
              <div style={{ ...muted, fontSize: '11px' }}>{isOver ? 'Überzogen' : 'Verbleibend'}</div>
              <div style={{ fontWeight: 950, color: warnColor }}>{isOver ? `+${euro(-item.remaining)}` : euro(item.remaining)}</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
            {item.id && <button onClick={onEdit} style={smBtn('#2563eb')}>Edit</button>}
            {item.id && <button onClick={onDelete} style={smBtn('#b42318')}>Del</button>}
            {!item.id && <button onClick={onEdit} style={smBtn('#0a6f4f')}>Budget setzen</button>}
          </div>
        </div>
      </div>

      {isOver && item.planned > 0 && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#9f1d12', background: '#fff1ef', padding: '6px 10px', borderRadius: '6px', fontWeight: 700 }}>
          ⚠ Budget voraussichtlich überschritten ({euro(Math.abs(item.remaining))} zu viel)
        </div>
      )}
      {!isOver && item.remaining !== null && item.remaining >= 0 && item.projected > item.budget && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#92400e', background: '#fffbeb', padding: '6px 10px', borderRadius: '6px', fontWeight: 700 }}>
          ⚠ Budget voraussichtlich überschritten mit geplanten Zahlungen
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function BudgetPage() {
  const [tab,  setTab]  = useState('monat');
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState('');

  const load = useCallback(async () => {
    setErr('');
    try { const r = await api.get('/budgets'); setData(r.data); }
    catch (e) { setErr(e.response?.data?.error || e.message || 'Ladefehler'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#064734', fontSize: '25px' }}>Budget-Ziele</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>Monatslimits und Kategorie-Budgets für flexible Zeiträume.</p>
        </div>
        <button onClick={load} style={btn('#334155')}>Aktualisieren</button>
      </div>

      {err && <Notice tone="red">{err}</Notice>}

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid #d8e4dd', paddingBottom: '0' }}>
        {[['monat','Monats-Ziele'],['kategorie','Kategorie-Budgets']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ background: 'none', border: 'none', padding: '10px 16px', cursor: 'pointer', fontWeight: tab===k ? 900 : 700, color: tab===k ? '#0a6f4f' : '#61726b', borderBottom: tab===k ? '2px solid #0a6f4f' : '2px solid transparent', marginBottom: '-2px', fontSize: '15px' }}>{l}</button>
        ))}
      </div>

      {tab === 'monat'     && <MonatsZieleTab     data={data} onReload={load} />}
      {tab === 'kategorie' && <KategorieBudgetsTab categories={data?.categories || []} />}
    </div>
  );
}
